import { CalendarX2 } from 'lucide-react';
import type { LinkBookability } from '../../services/interviewLinksApi';
import { bottleneckNames } from './bottlenecks';
import { dayFmt } from './format';

/**
 * One line of truth about a link's booking page, shown where a recruiter is
 * about to send that page to a candidate. The failure this prevents: mailing a
 * link whose window is already fully booked, so the candidate's first
 * impression of FINN is an empty calendar.
 *
 * `unknown` (calendars unreadable) renders nothing — the check is advisory,
 * and a warning that fires on missing credentials rather than real state
 * would train recruiters to ignore it.
 */
export function BookabilitySummary({
  bookability,
}: {
  bookability: LinkBookability | null | undefined;
}) {
  if (!bookability || bookability.risk === 'unknown') return null;

  // Switched off, or its role closed in Lever: no slot count is meaningful, and
  // minting an invite here will be refused outright — so say that instead.
  if (bookability.risk === 'closed') {
    // Two causes with different fixes: the proxy names a closed Lever posting in `slotError`.
    const roleClosed = /role is closed/i.test(bookability.slotError || '');
    return (
      <div className="mt-2 flex items-start gap-2 rounded-lg border border-[#EEC79A] bg-[#FCEEE2] p-2.5 text-xs text-[#5C3608]">
        <CalendarX2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#854F0B]" />
        <span>
          <span className="font-semibold">Nobody can book this link</span> —{' '}
          {roleClosed
            ? 'its role is closed in Lever. Re-open the posting there to invite candidates again.'
            : 'it is closed. Reopen it in the link editor before sending it to a candidate.'}
        </span>
      </div>
    );
  }

  if (bookability.risk === 'ok') {
    return (
      <p className="mt-2 text-xs text-gray-500">
        {bookability.slotsInWindow} bookable slot{bookability.slotsInWindow === 1 ? '' : 's'} in
        the next {bookability.offeredRangeDays} days
        {bookability.firstBookable
          ? `, first: ${dayFmt.format(new Date(bookability.firstBookable))}`
          : ''}
        .
      </p>
    );
  }

  const blocked = bottleneckNames(bookability.perInterviewer);

  return (
    <div className="mt-2 flex items-start gap-2 rounded-lg border border-[#EEC79A] bg-[#FCEEE2] p-2.5 text-xs text-[#5C3608]">
      <CalendarX2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#854F0B]" />
      <span>
        {bookability.risk === 'empty' ? (
          <>
            <span className="font-semibold">
              A candidate opening this link right now sees NO free slots
            </span>{' '}
            in its {bookability.offeredRangeDays}-day window.
          </>
        ) : (
          <>
            <span className="font-semibold">
              Only {bookability.slotsInWindow} slot{bookability.slotsInWindow === 1 ? '' : 's'} left
            </span>{' '}
            in the {bookability.offeredRangeDays}-day window.
          </>
        )}
        {blocked.length > 0
          ? ` ${blocked.join(', ')} ${blocked.length === 1 ? 'has' : 'have'} no free slot — consider fixing the link before sending it.`
          : ' Consider fixing the link before sending it.'}
      </span>
    </div>
  );
}
